import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Clock } from "lucide-react";
import api from "@/lib/api";
import useMeta from "@/hooks/useMeta";

const CATEGORIES = [
    { slug: null, key: "blog.cat_all" },
    { slug: "recomendaciones", key: "blog.cat_recommendations" },
    { slug: "autores", key: "blog.cat_authors" },
    { slug: "generos", key: "blog.cat_genres" },
    { slug: "novedades", key: "blog.cat_new" },
    { slug: "librerias", key: "blog.cat_bookshops" },
];

function formatDate(iso, lang) {
    if (!iso) return "";
    try {
        return new Intl.DateTimeFormat(lang, {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(new Date(iso));
    } catch {
        return iso.slice(0, 10);
    }
}

export default function Blog() {
    const { t, i18n } = useTranslation();
    const [posts, setPosts] = useState(null);
    const [activeCategory, setActiveCategory] = useState(null);

    useMeta({
        title: t("blog.meta_title"),
        description: t("blog.meta_description"),
        canonical: "https://recomendalibros.com/blog",
    });

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const params = activeCategory ? { category: activeCategory } : {};
                const { data } = await api.get("/blog/posts", { params });
                if (!alive) return;
                setPosts(data || []);
            } catch {
                if (alive) setPosts([]);
            }
        })();
        return () => {
            alive = false;
        };
    }, [activeCategory]);

    return (
        <main
            data-testid="blog-index"
            className="min-h-[calc(100vh-73px)] max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24"
        >
            <div className="border-b border-[var(--border)] pb-10 mb-12 text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--border-strong)] text-xs uppercase tracking-widest text-[var(--accent)] mb-6">
                    {t("blog.eyebrow")}
                </div>
                <h1
                    className="font-display text-4xl md:text-6xl text-[var(--ink)] tracking-tight mb-5 text-left"
                    style={{ fontVariationSettings: '"wght" 700, "wdth" 78, "opsz" 144' }}
                >
                    {t("blog.title")}
                </h1>
                <p className="text-[var(--ink-muted)] text-lg leading-relaxed max-w-2xl text-left">
                    {t("blog.lead")}
                </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-12">
                {CATEGORIES.map((c) => {
                    const active = activeCategory === c.slug;
                    return (
                        <button
                            key={c.slug || "all"}
                            type="button"
                            data-testid={`blog-cat-${c.slug || "all"}`}
                            onClick={() => setActiveCategory(c.slug)}
                            className={[
                                "px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors",
                                active
                                    ? "border-[var(--accent)] bg-[var(--accent)]/15 text-[var(--accent)]"
                                    : "border-[var(--border-strong)] text-[var(--ink-muted)] hover:border-[var(--ink)] hover:text-[var(--ink)]",
                            ].join(" ")}
                        >
                            {t(c.key)}
                        </button>
                    );
                })}
            </div>

            {posts === null ? (
                <p className="text-[var(--ink-muted)] italic animate-slow-pulse">
                    {t("blog.loading")}
                </p>
            ) : posts.length === 0 ? (
                <p
                    data-testid="blog-empty"
                    className="text-[var(--ink-muted)] italic border border-dashed border-[var(--border-strong)] p-8 text-center"
                >
                    {t("blog.empty")}
                </p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                    {posts.map((p) => (
                        <Link
                            key={p.id}
                            to={`/blog/${p.slug}`}
                            data-testid={`blog-post-card-${p.slug}`}
                            className="group flex flex-col border-t border-[var(--border-strong)] pt-6 hover:border-[var(--accent)] transition-colors"
                        >
                            {p.cover_url && (
                                <div className="aspect-[16/10] bg-[var(--bg-elevated)] overflow-hidden mb-4">
                                    <img
                                        src={p.cover_url}
                                        alt={p.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                </div>
                            )}
                            <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                                {t(`blog.cat_${categoryKey(p.category)}`)}
                            </p>
                            <h2
                                className="font-display text-2xl md:text-[1.7rem] text-[var(--ink)] leading-tight mb-3 group-hover:text-[var(--accent)] transition-colors"
                                style={{ fontVariationSettings: '"wght" 500, "wdth" 90, "opsz" 32' }}
                            >
                                {p.title}
                            </h2>
                            <p className="text-[var(--ink-muted)] text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
                                {p.excerpt}
                            </p>
                            <div className="flex items-center justify-between text-xs text-[var(--ink-dim)]">
                                <span>{formatDate(p.published_at, i18n.language)}</span>
                                <span className="inline-flex items-center gap-1">
                                    <Clock size={11} strokeWidth={1.8} />
                                    {t("blog.read_min", { count: p.read_time_min || 1 })}
                                </span>
                            </div>
                            <span className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[var(--accent)] group-hover:gap-2.5 transition-all">
                                {t("blog.read_more")}
                                <ArrowRight size={12} strokeWidth={1.8} />
                            </span>
                        </Link>
                    ))}
                </div>
            )}
        </main>
    );
}

// Keep the cat_* i18n keys consistent with `CATEGORIES` mapping above.
function categoryKey(cat) {
    switch (cat) {
        case "recomendaciones":
            return "recommendations";
        case "autores":
            return "authors";
        case "generos":
            return "genres";
        case "novedades":
            return "new";
        case "librerias":
            return "bookshops";
        default:
            return "all";
    }
}
